/**
 * Finding a row in the forecast grid: the order siblings are listed in, and the search box.
 *
 * Both work on the hierarchy **tree**, before it is flattened into table rows. The search used
 * to run over the flattened rows, which only hold what is currently expanded — so a customer or
 * product sitting under a collapsed parent could not be found at all.
 */

/** `revenue` is the order the plan loads in (largest first); `name` is A–Z at every level. */
export type GridSortOrder = "revenue" | "name"

export const GRID_SORT_STORAGE_KEY = "forecast_grid_sort_order"
export const DEFAULT_GRID_SORT_ORDER: GridSortOrder = "revenue"

/** The slice of a hierarchy node these helpers read; every forecast view's node satisfies it. */
export interface GridFindNode {
  id: string
  name: string
  children?: GridFindNode[]
}

export function parse_grid_sort_order(raw: string | null | undefined): GridSortOrder {
  return raw === "name" || raw === "revenue" ? raw : DEFAULT_GRID_SORT_ORDER
}

/** The viewer's saved choice. Storage can be unavailable (private mode); fall back quietly. */
export function read_grid_sort_order(): GridSortOrder {
  try {
    return parse_grid_sort_order(localStorage.getItem(GRID_SORT_STORAGE_KEY))
  } catch {
    return DEFAULT_GRID_SORT_ORDER
  }
}

export function save_grid_sort_order(order: GridSortOrder): void {
  try {
    localStorage.setItem(GRID_SORT_STORAGE_KEY, order)
  } catch {
    // The choice still applies for this visit.
  }
}

/**
 * A–Z as a person reads a customer list: case and accents do not matter, and numbers sort by
 * value ("Region 2" before "Region 10").
 */
export function compare_names(a: string, b: string): number {
  return a.trim().localeCompare(b.trim(), undefined, { sensitivity: "base", numeric: true })
}

/**
 * Order the grid's rows. Siblings are sorted among themselves at every level, so a child never
 * leaves its parent. `revenue` returns the tree untouched: the plan already loads largest
 * first, and re-ranking on every keystroke would make rows jump while they are being edited.
 */
export function sort_hierarchy<T extends GridFindNode>(nodes: T[], order: GridSortOrder): T[] {
  if (order === "revenue") return nodes
  return nodes
    .map((node: T) =>
      Array.isArray(node.children) && node.children.length > 0
        ? { ...node, children: sort_hierarchy(node.children as T[], order) }
        : node,
    )
    .sort((a: T, b: T) => compare_names(a.name, b.name))
}

export interface GridSearchResult<T> {
  /** The tree pruned to matches, their ancestors and their descendants. */
  nodes: T[]
  /** Ancestors of a match: opened so the match is on screen without the user expanding to it. */
  expand_ids: Set<string>
  /** How many rows matched by their own name. */
  match_count: number
}

export function normalize_search_term(term: string): string {
  return term.trim().toLowerCase()
}

/**
 * Filter the grid to rows whose name contains `term` (case-insensitive).
 *
 * - A matching row keeps everything under it, so it still expands down to its SKUs.
 * - A matching child keeps its parents, which are opened to show it; their own numbers are
 *   left as they are (the parent's whole total, not the sum of what is shown).
 * - Siblings that neither match nor contain a match are dropped.
 */
export function filter_hierarchy_by_search<T extends GridFindNode>(nodes: T[], term: string): GridSearchResult<T> {
  const needle = normalize_search_term(term)
  const expand_ids = new Set<string>()
  if (!needle) return { nodes, expand_ids, match_count: 0 }
  let match_count = 0

  function count_matches(list: GridFindNode[]): void {
    for (const node of list) {
      if (node.name.toLowerCase().includes(needle)) match_count += 1
      if (node.children) count_matches(node.children)
    }
  }

  function prune(list: T[]): T[] {
    const kept: T[] = []
    for (const node of list) {
      if (node.name.toLowerCase().includes(needle)) {
        match_count += 1
        if (node.children) count_matches(node.children)
        kept.push(node)
        continue
      }
      const children = Array.isArray(node.children) ? prune(node.children as T[]) : []
      if (children.length > 0) {
        expand_ids.add(node.id)
        kept.push({ ...node, children })
      }
    }
    return kept
  }

  return { nodes: prune(nodes), expand_ids, match_count }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  interface TestNode extends GridFindNode {
    children?: TestNode[]
    dollars?: number
  }

  function names(nodes: TestNode[]): string[] {
    return nodes.map((node: TestNode) => node.name)
  }

  // Largest first, as the plan loads.
  const tree: TestNode[] = [
    {
      id: "wfm",
      name: "Whole Foods",
      children: [
        { id: "wfm-yuzu", name: "Yuzu Sparkling Water (SS)" },
        { id: "wfm-punch", name: "Paradise Punch Sparkling Water (SS)" },
      ],
    },
    {
      id: "ahold",
      name: "AHOLD - Giant Carlisle",
      children: [{ id: "ahold-calamansi", name: "Calamansi Sparkling Water (SS)" }],
    },
    {
      id: "sprouts",
      name: "sprouts",
      children: [
        {
          id: "sprouts-line",
          name: "Sparkling Water - SS",
          children: [{ id: "sprouts-punch", name: "Paradise Punch Sparkling Water (SS)" }],
        },
      ],
    },
    { id: "target", name: "Target" },
  ]

  describe("sort_hierarchy", () => {
    it("leaves the plan's own order alone for revenue", () => {
      expect(sort_hierarchy(tree, "revenue")).toBe(tree)
    })

    it("sorts customers A–Z without regard to case", () => {
      expect(names(sort_hierarchy(tree, "name"))).toEqual(["AHOLD - Giant Carlisle", "sprouts", "Target", "Whole Foods"])
    })

    it("sorts each parent's children among themselves, keeping them under it", () => {
      const sorted = sort_hierarchy(tree, "name")
      const whole_foods = sorted.find((node: TestNode) => node.id === "wfm")
      expect(names(whole_foods?.children ?? [])).toEqual([
        "Paradise Punch Sparkling Water (SS)",
        "Yuzu Sparkling Water (SS)",
      ])
      // Every child is still under the parent it started with.
      expect(sorted.find((node: TestNode) => node.id === "ahold")?.children?.[0].id).toBe("ahold-calamansi")
    })

    it("does not reorder the tree it was given", () => {
      sort_hierarchy(tree, "name")
      expect(names(tree)).toEqual(["Whole Foods", "AHOLD - Giant Carlisle", "sprouts", "Target"])
      expect(names(tree[0].children ?? [])[0]).toBe("Yuzu Sparkling Water (SS)")
    })

    it("orders numbers by value", () => {
      const regions: TestNode[] = [
        { id: "10", name: "Region 10" },
        { id: "2", name: "Region 2" },
      ]
      expect(names(sort_hierarchy(regions, "name"))).toEqual(["Region 2", "Region 10"])
    })
  })

  describe("parse_grid_sort_order", () => {
    it("falls back to revenue for anything it does not recognise", () => {
      expect(parse_grid_sort_order("name")).toBe("name")
      expect(parse_grid_sort_order("revenue")).toBe("revenue")
      expect(parse_grid_sort_order(null)).toBe("revenue")
      expect(parse_grid_sort_order("alphabetical")).toBe("revenue")
    })
  })

  describe("filter_hierarchy_by_search", () => {
    it("returns the tree untouched for an empty search", () => {
      const result = filter_hierarchy_by_search(tree, "   ")
      expect(result.nodes).toBe(tree)
      expect(result.expand_ids.size).toBe(0)
    })

    it("isolates a customer, ignoring case, and keeps its children", () => {
      const result = filter_hierarchy_by_search(tree, "WHOLE")
      expect(names(result.nodes)).toEqual(["Whole Foods"])
      expect(result.nodes[0].children).toHaveLength(2)
      // The match itself is not forced open; the user expands it as usual.
      expect(result.expand_ids.size).toBe(0)
      expect(result.match_count).toBe(1)
    })

    it("matches anywhere in the name", () => {
      expect(names(filter_hierarchy_by_search(tree, "giant").nodes)).toEqual(["AHOLD - Giant Carlisle"])
    })

    it("finds a child under a collapsed parent and opens the path to it", () => {
      const result = filter_hierarchy_by_search(tree, "calamansi")
      expect(names(result.nodes)).toEqual(["AHOLD - Giant Carlisle"])
      expect(names(result.nodes[0].children ?? [])).toEqual(["Calamansi Sparkling Water (SS)"])
      expect([...result.expand_ids]).toEqual(["ahold"])
    })

    it("drops siblings that do not match and keeps every level above a deep match", () => {
      const result = filter_hierarchy_by_search(tree, "paradise punch")
      expect(names(result.nodes)).toEqual(["Whole Foods", "sprouts"])
      expect(names(result.nodes[0].children ?? [])).toEqual(["Paradise Punch Sparkling Water (SS)"])
      expect([...result.expand_ids].sort()).toEqual(["sprouts", "sprouts-line", "wfm"])
      expect(result.match_count).toBe(2)
    })

    it("counts matches under a matching parent too", () => {
      // The product line "Sparkling Water - SS" matches, and so does the SKU beneath it.
      const result = filter_hierarchy_by_search(tree, "sprouts")
      expect(result.match_count).toBe(1)
      expect(filter_hierarchy_by_search(tree, "sparkling").match_count).toBe(5)
    })

    it("returns nothing when nothing matches", () => {
      const result = filter_hierarchy_by_search(tree, "costco")
      expect(result.nodes).toEqual([])
      expect(result.match_count).toBe(0)
    })

    it("does not change the tree it was given", () => {
      filter_hierarchy_by_search(tree, "calamansi")
      expect(tree).toHaveLength(4)
      expect(tree[0].children).toHaveLength(2)
    })
  })
}
